import React, { useMemo, useState, useEffect, useCallback } from 'react';
import propTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Image,
  Stack,
  useBreakpointValue,
  useDisclosure,
  Flex,
  Spacer,
  Popover,
  Portal,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@chakra-ui/react';

import { Element, scroller } from 'react-scroll';
import * as ga from '../lib/ga';

import { Button } from '@outdoorly/button';
import { Alert } from '@outdoorly/alert';
import { Text } from '@outdoorly/text';
import { ProductAttributeSelect } from '@outdoorly/select';
import { Container } from '@outdoorly/container';
import { Modal } from '@outdoorly/modal';

import { AddReview } from './add-review';

import {
  TruckIcon,
  MapPinIcon,
  PackageIcon,
  RefreshCcwIcon,
  InfoIcon,
} from '@outdoorly/icons';
import {
  findVariantsForAttributeValues,
  getIndefiniteArticle,
  getProductVariantAttributes,
} from '@outdoorly/util';
import { Field } from '@outdoorly/input';
import { StarRating } from '@outdoorly/star-rating';
import { Divider } from '@outdoorly/divider';
import { gql, useQuery } from '@apollo/client';
import { useAuth } from '../providers/auth';
import { makeNetworkStatus } from '../hooks/make-network-status';
import { useAddToBag } from '../hooks/use-add-to-bag';
import { ProductCarouselWithThumbnail } from './product/ProductCarouselWithThumbnails';
import { ProductHeader } from './product/ProductHeader';
import { ProductPrice } from './product/ProductPrice';
import { getAnalyticsFromProduct } from '../lib/ga/utils';

const GET_PRODUCT_REVIEWS = gql`
  query Product($slug: String!, $first: Int!, $after: String) {
    product(slug: $slug) {
      id
      rating
      reviews(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
        edges {
          node {
            id
            headline
            reviewText
            starRating
            user {
              publicName
            }
          }
        }
      }
    }
  }
`;

const productType = propTypes.shape({
  slug: propTypes.string.isRequired,
  name: propTypes.string,
  rating: propTypes.number,
  description: propTypes.string,
  inStock: propTypes.bool,
  brand: propTypes.shape({
    name: propTypes.string,
    returnPolicy: propTypes.string,
    shippingDescription: propTypes.string,
    shippingSpeed: propTypes.string,
    shipsFrom: propTypes.shape({
      country: propTypes.shape({
        country: propTypes.string,
      }),
    }),
    slug: propTypes.string,
    returnPolicyCustomNDays: propTypes.number,
    returnAndExchangeText: propTypes.string,
    shippingSpeedUpperBound: propTypes.number,
    shippingSpeedLowerBound: propTypes.number,
  }),
  tags: propTypes.arrayOf(
    propTypes.shape({
      tagName: propTypes.string,
    })
  ),
  sizeGuide: propTypes.shape({
    url: propTypes.string,
  }),
  techSpecs: propTypes.string,
  alerts: propTypes.string,
  reviews: propTypes.shape({
    totalCount: propTypes.number,
    edges: propTypes.arrayOf(
      propTypes.shape({
        node: propTypes.shape({
          id: propTypes.string,
        }),
      })
    ),
  }),
  pricing: propTypes.shape({
    priceRangeUndiscounted: propTypes.shape(),
    priceRange: propTypes.shape(),
  }),
  thumbnail: propTypes.shape({
    url: propTypes.string,
  }),
  images: propTypes.arrayOf(
    propTypes.shape({
      id: propTypes.string.isRequired,
      url: propTypes.string,
      alt: propTypes.string,
    })
  ),
  variants: propTypes.arrayOf(
    propTypes.shape({
      id: propTypes.string,
      images: propTypes.arrayOf(
        propTypes.shape({
          id: propTypes.string.isRequired,
          url: propTypes.string,
          alt: propTypes.string,
        })
      ),
      pricing: propTypes.shape(),
      attributes: propTypes.arrayOf(
        propTypes.shape({
          id: propTypes.string,
          name: propTypes.string,
          values: propTypes.arrayOf(
            propTypes.shape({
              id: propTypes.string,
              name: propTypes.string,
              color: propTypes.string,
            })
          ),
        })
      ),
    })
  ),
});

const RETURN_POLICY_MAP = {
  NO_RETURNS: 'No returns',
  FINAL_SALE_AND_NO_RETURNS: 'Final sale and no returns',
  FIFTEEN_DAY_RETURNS: '15 day returns',
  THIRTY_DAY_RETURNS: '30 day returns',
};

const AdditionalInfo = ({ text, icon, fullText }) => {
  const lines = fullText?.split('\n');
  return (
    <Stack direction="row" align="center" spacing={2}>
      {icon}
      <Text color="scheme.textSecondary" as="h5">
        {text}
      </Text>
      {fullText && (
        <Popover
          placement="bottom"
          size="lg"
          variant="OD-tooltip"
          trigger="hover"
        >
          <PopoverTrigger>
            <Box>
              <InfoIcon
                cursor="pointer"
                aria-label="More info"
                color="scheme.textSecondary"
              />
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent>
              <PopoverArrow left={'-4px !important'} />
              <PopoverBody maxH="md" padding="15px">
                {lines.map((line, index) => (
                  <>
                    <Text key={index} fontSize="sm">
                      {line}
                    </Text>
                    {index !== lines.length - 1 && <br />}
                  </>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </Stack>
  );
};

AdditionalInfo.propTypes = {
  text: propTypes.string,
  icon: propTypes.node,
  fullText: propTypes.string,
};

const SizeGuide = ({ src }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      {/* TODO: The link variant style is causing the 'Size guide' button to jut out of the container, adding mr={2} as a workaround until this is investigated */}
      <Button variant="link" onClick={onOpen}>
        Size guide
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} title="Size Guide">
        <Image minWidth={16} width="100%" src={src} alt="Size Guide" />
      </Modal>
    </>
  );
};

SizeGuide.propTypes = {
  src: propTypes.string,
};

export const ProductActionBox = ({
  product,
  setFocusedVariant,
  breakpoint,
  onReadMore,
  ...props
}) => {
  const { isNotAuthed } = useAuth();

  const { watch, control, setValue } = useForm();
  const rawChoices = watch();
  const [valueToIgnore, setValueToIgnore] = useState(null);
  const choices = Object.entries(rawChoices).reduce(
    (acc, [key, choice]) => ({ ...acc, [key]: choice?.value }),
    {}
  );

  const choicesWithIgnoredValue = Object.entries(rawChoices).reduce(
    (acc, [key, choice]) => ({
      ...acc,
      [key]: key === valueToIgnore ? undefined : choice?.value,
    }),
    {}
  );

  const filteredVariantsWithIgnoredValue = findVariantsForAttributeValues(
    product,
    choicesWithIgnoredValue
  );
  const hasOnlyOneVariant = product.variants.length === 1;

  const allChoicesEmpty = Object.values(choices).every((value) => !value);
  const allChoicesCompleted = Object.values(choices).every((value) => !!value);

  const filteredVariants = hasOnlyOneVariant
    ? product.variants
    : findVariantsForAttributeValues(product, choices);

  useEffect(() => {
    if (filteredVariants.length && !allChoicesEmpty) {
      setFocusedVariant({
        variant: filteredVariants[0],
        isDefiniteVariant: filteredVariants.length === 1,
      });
    }
  }, [filteredVariants[0]?.id, allChoicesEmpty, setFocusedVariant]);

  const definiteCurrentVariant =
    filteredVariants.length === 1 ? filteredVariants[0] : undefined;

  const variantIsUnavailable =
    !allChoicesEmpty && allChoicesCompleted && !definiteCurrentVariant;

  const outOfStock = filteredVariants.every(
    (variant) => variant.quantity === 0
  );

  const soldOutSet = new Set();
  const inStockSet = new Set();

  const variantsToCheckStock = valueToIgnore
    ? filteredVariantsWithIgnoredValue
    : filteredVariants;

  for (let variant of variantsToCheckStock) {
    const variantIsOutOfStock = variant.quantity === 0;
    for (let attributeValue of variant.attributes) {
      const id = attributeValue.values[0].id;
      const itemIsOutOfStock = variantIsOutOfStock && !inStockSet.has(id);
      const itemIsInStock = !variantIsOutOfStock;
      if (itemIsOutOfStock) {
        soldOutSet.add(id);
      } else if (itemIsInStock && soldOutSet.has(id)) {
        inStockSet.add(id);
        soldOutSet.delete(id);
      } else if (itemIsInStock) {
        inStockSet.add(id);
      }
    }
  }

  const setAttributeToIgnore = (input) => {
    const selection = input.name;
    const ignoredValue = attributes.find(
      (attributeValue) => attributeValue.attribute.id === selection
    );
    if (ignoredValue) {
      setValueToIgnore(ignoredValue?.attribute?.id);
    }
  };

  const resetOthers = (id) => {
    attributes.map((attributeValue) => {
      if (attributeValue.attribute.id !== id) {
        setValue(attributeValue.attribute.id, '');
      }
    });
  };

  const attributes = useMemo(
    () =>
      Object.values(getProductVariantAttributes(product ?? { variants: [] })),
    [product]
  );

  // ---- CART FUNCTIONALITY ----

  const [addingToCart, setAddingToCart] = useState(false);
  const addToBag = useAddToBag();

  const addToCart = useCallback(() => {
    setAddingToCart(true);
    addToBag(definiteCurrentVariant).finally(() => setAddingToCart(false));
  }, [addToBag, definiteCurrentVariant]);

  // ---- Replacing newlines ----
  const description = useMemo(
    () => product?.description?.replace('\n', '<br/>'),
    [product]
  );

  // ---- RENDER ----

  return (
    <Box mb={6} top={0} {...props}>
      <Stack spacing={6}>
        {breakpoint === 'desktop' && (
          <>
            <ProductHeader
              product={product}
              definiteCurrentVariant={definiteCurrentVariant}
              isDesktop={breakpoint === 'desktop'}
              onReadMore={onReadMore}
            />
            <Divider />

            {/* <Stack
          direction="row"
          align="center"
          paddingY={2}
          borderTopWidth={1}
          borderBottomWidth={1}
          borderColor="scheme.border"
        >
          <HeartIcon
            role="presentation"
            focusable={false}
            aria-hidden={true}
            color={'scheme.controlInactive'}
            strokeWidth={1}
            minWidth={5}
            size={30}
          />
          <Box w="100%">
            <Text color="scheme.textSecondary">Theo Satloff and 4 others</Text>
          </Box>
          <Menu>
            <MenuButton
              as={Button}
              variant="link"
              rightIcon={<MoreHorizontalIcon />}
            />
            <MenuList fontSize="sm">
              <MenuItem
                as="a"
                onClick={() => {
                  // TODO: In share menu, copy current URL button logic to be implemented
                }}
              >
                Copy Link
              </MenuItem>
              <MenuItem
                onClick={() => {
                  // TODO: In share menu, open Twitter share link logic to be implemented
                }}
              >
                Share on Twitter
              </MenuItem>
            </MenuList>
          </Menu>
        </Stack> */}

            <Stack spacing={3}>
              <Text>
                <Box noOfLines={10}>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: description,
                    }}
                    sx={{ listStyle: 'inside' }}
                  />
                </Box>
              </Text>
              <Button
                alignSelf="flex-start"
                variant="link"
                onClick={() => {
                  onReadMore?.();
                  scroller.scrollTo('description', {
                    duration: 600,
                    offset: -25,
                    smooth: 'easeInOut',
                  });
                }}
              >
                Read more
              </Button>
            </Stack>
          </>
        )}

        {!hasOnlyOneVariant && (
          <Stack spacing={6} as="form" mt={[4, 4, 4, 0]}>
            {attributes.map((attribute) => {
              const attributeId = attribute.attribute.id;
              return (
                <Field label={attribute.attribute.name} key={attributeId}>
                  <Controller
                    as={ProductAttributeSelect}
                    name={attributeId}
                    control={control}
                    setAttributeToIgnore={setAttributeToIgnore}
                    resetOthers={resetOthers}
                    options={Object.values(attribute.values).map((value) => {
                      return {
                        label: value.name,
                        value: value.id,
                        isSoldOut: soldOutSet.has(value.id),
                        isUnavailable:
                          !inStockSet.has(value.id) &&
                          !soldOutSet.has(value.id),
                      };
                    })}
                    isSearchable={false}
                    placeholder={`Pick ${getIndefiniteArticle(
                      attribute.attribute.name.toLowerCase()
                    )} ${attribute.attribute.name.toLowerCase()}`}
                  />
                </Field>
              );
            })}
          </Stack>
        )}

        {/* TODO: Add to Cart will most likely be pinned to the bottom of the mobile view when scrolling */}
        <Button
          isDisabled={!definiteCurrentVariant || isNotAuthed || outOfStock}
          isLoading={addingToCart}
          onClick={addToCart}
          width="100%"
          variant="solid"
          colorScheme="red"
          mt={[4, 4, 4, 0]}
          _disabled={{
            backgroundColor: 'gray.75',
            color: 'scheme.controlInactive',
            cursor: 'not-allowed',
            pointerEvents: 'none',
          }}
        >
          {variantIsUnavailable
            ? 'Unavailable'
            : outOfStock
            ? 'Out of stock'
            : isNotAuthed
            ? 'Log in to buy'
            : 'Add to Bag'}
        </Button>
        {product?.alerts && (
          <Alert
            title="Notice"
            status="warning"
            description={product?.alerts}
          />
        )}
        {product?.sizeGuide && <SizeGuide src={product?.sizeGuide.url} />}

        <Text fontWeight="500" as="h3">
          Additional Information
        </Text>
        <Stack spacing={3}>
          {product?.brand.returnPolicy && (
            <AdditionalInfo
              icon={
                <RefreshCcwIcon
                  role="presentation"
                  focusable={false}
                  aria-hidden={true}
                  color={'scheme.iconRegular'}
                  strokeWidth={2}
                  minWidth={5}
                />
              }
              text={
                RETURN_POLICY_MAP[product?.brand.returnPolicy] ??
                `${product?.brand.returnPolicyCustomNDays} day returns`
              }
              fullText={product?.brand.returnAndExchangeText}
            />
          )}
          {product?.brand.shipsFrom && (
            <AdditionalInfo
              icon={
                <MapPinIcon
                  role="presentation"
                  focusable={false}
                  aria-hidden={true}
                  color={'scheme.iconRegular'}
                  strokeWidth={2}
                  minWidth={5}
                />
              }
              text={`Ships from ${product.brand.shipsFrom.country.country}`}
            />
          )}
          {product?.brand.shippingDescription && (
            <AdditionalInfo
              icon={
                <PackageIcon
                  role="presentation"
                  focusable={false}
                  aria-hidden={true}
                  color={'scheme.iconRegular'}
                  strokeWidth={2}
                  minWidth={5}
                />
              }
              text={product.brand.shippingDescription}
            />
          )}
          {product?.brand.shippingSpeedUpperBound && (
            <AdditionalInfo
              icon={
                <TruckIcon
                  role="presentation"
                  focusable={false}
                  aria-hidden={true}
                  color={'scheme.iconRegular'}
                  strokeWidth={2}
                  minWidth={5}
                />
              }
              text={`Ships in ${product.brand.shippingSpeedLowerBound} to ${product.brand.shippingSpeedUpperBound} days`}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

ProductActionBox.propTypes = {
  product: productType,
  setFocusedVariant: propTypes.func,
  focusedVariant: propTypes.object,
  onReadMore: propTypes.func,
  breakpoint: propTypes.string,
};

const ProductDescriptionBox = ({ product, ...props }) => {
  const { isAuthed } = useAuth();

  const { data: reviewData, networkStatus, fetchMore } = useQuery(
    GET_PRODUCT_REVIEWS,
    {
      variables: { slug: product?.slug, first: 5 },
      skip: !product?.slug,
      notifyOnNetworkStatusChange: true,
    }
  );

  const hasMoreReviews = reviewData?.product.reviews.pageInfo.hasNextPage;
  const { fetchingMore: loadingMoreReviews } = makeNetworkStatus(networkStatus);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // ---- Replacing newlines ----
  const description = useMemo(
    () => product?.description?.replace('\n', '<br/>'),
    [product]
  );

  const techSpecs = useMemo(() => product?.techSpecs?.replace('\n', '<br/>'), [
    product,
  ]);

  return (
    <Box {...props}>
      <Accordion defaultIndex={[0]} allowMultiple marginTop="15px">
        <AccordionItem>
          <AccordionButton paddingX={0} paddingY={5}>
            <Box flex="1" textAlign="left">
              <Element name="description" />
              <Text fontWeight="500">Product Description</Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel paddingX={0} paddingY={5}>
            <Text
              as={Box}
              dangerouslySetInnerHTML={{
                __html: description,
              }}
              sx={{ listStyle: 'inside' }}
            />
          </AccordionPanel>
        </AccordionItem>
        {techSpecs && (
          <AccordionItem>
            <AccordionButton paddingX={0} paddingY={5}>
              <Box flex="1" textAlign="left">
                <Text fontWeight="500">Tech Specs</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel paddingX={0} paddingY={5}>
              <Text
                as={Box}
                dangerouslySetInnerHTML={{
                  __html: techSpecs,
                }}
                sx={{ listStyle: 'inside' }}
              />
            </AccordionPanel>
          </AccordionItem>
        )}
        {/* TODO: Supported Causes */}
        <AccordionItem borderBottom="none">
          <AccordionButton paddingX={0} paddingY={5}>
            <Box flex="1" textAlign="left">
              <Flex direction="row" align="center">
                <Box>
                  <Element name="reviews" />
                  <Text fontWeight="500">Reviews</Text>
                </Box>
                <Spacer />
                {product?.rating && (
                  <Stack direction="row" align="center" spacing={2} mr={2}>
                    <StarRating rating={product?.rating} />
                    <Text>{product?.rating.toFixed(1)}</Text>
                  </Stack>
                )}
              </Flex>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel paddingX={0} paddingY={5}>
            <Stack spacing={8}>
              {reviewData?.product.reviews.edges.length ? (
                reviewData?.product.reviews.edges
                  .map((edge) => edge.node)
                  .map((review) => (
                    <Stack spacing={8} key={review.id}>
                      <Stack spacing={4}>
                        <StarRating rating={review.starRating} />
                        <Text fontWeight="500">{review.headline}</Text>
                        <Text>{review.reviewText}</Text>
                      </Stack>
                      <Text color="scheme.textSecondary">
                        {review.user.publicName}
                      </Text>
                    </Stack>
                  ))
              ) : (
                <Text color="scheme.textSecondary">No reviews yet</Text>
              )}
              <Stack direction="row" align="center" spacing={4}>
                {hasMoreReviews && (
                  <Button
                    paddingX={0}
                    variant="link"
                    onClick={() =>
                      fetchMore({
                        variables: {
                          first: 10,
                          after: reviewData?.product.reviews.pageInfo.endCursor,
                        },
                      })
                    }
                    isDisabled={loadingMoreReviews}
                  >
                    Read more reviews
                  </Button>
                )}
                {isAuthed && (
                  <>
                    <AddReview
                      product={product}
                      isOpen={isOpen}
                      onClose={onClose}
                    />
                    <Button paddingX={0} variant="link" onClick={onOpen}>
                      Write a Review
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

ProductDescriptionBox.propTypes = {
  product: productType,
  breakpoint: propTypes.string,
};

export const Product = ({ product }) => {
  useEffect(() => {
    if (product) {
      ga.event({
        action: 'view_item',
        type: 'full-view',
        ...getAnalyticsFromProduct(product),
      });
    }
  }, []);
  const [focusedVariant, setFocusedVariant] = useState(undefined);

  const breakpoint = useBreakpointValue({
    base: 'mobile',
    md: 'mobile',
    lg: 'desktop',
  });

  if (breakpoint === 'mobile') {
    return (
      <>
        <ProductCarouselWithThumbnail
          product={product}
          focusedVariant={focusedVariant?.variant}
          deviceType={breakpoint}
        />
        <ProductHeader
          product={product}
          isDesktop={false}
          definiteCurrentVariant={
            focusedVariant?.isDefiniteVariant
              ? focusedVariant?.variant
              : undefined
          }
        />
        <ProductPrice
          product={product}
          definiteCurrentVariant={
            focusedVariant?.isDefiniteVariant
              ? focusedVariant?.variant
              : undefined
          }
          isDesktop={false}
        />
        <ProductActionBox
          product={product}
          focusedVariant={focusedVariant?.variant}
          setFocusedVariant={setFocusedVariant}
          breakpoint={breakpoint}
          paddingX="gutter"
        />
        <ProductDescriptionBox
          product={product}
          breakpoint={breakpoint}
          paddingX="gutter"
        />
      </>
    );
  } else {
    return (
      <>
        <Container mt={6} mb={6}>
          <Flex>
            <Flex
              direction="column"
              minW="0"
              flexGrow={1}
            >
              <ProductCarouselWithThumbnail
                product={product}
                focusedVariant={focusedVariant?.variant}
                deviceType={breakpoint}
              />
              <ProductDescriptionBox
                product={product}
                breakpoint={breakpoint}
              />
            </Flex>
            <Flex direction="column" minW="500px" ml={6}>
              <ProductActionBox
                product={product}
                focusedVariant={focusedVariant?.variant}
                setFocusedVariant={setFocusedVariant}
                breakpoint={breakpoint}
                position="sticky"
              />
            </Flex>
          </Flex>
        </Container>
      </>
    );
  }
};

Product.propTypes = {
  product: productType,
};
